import { initializeApp } from "firebase/app";
import { 
  getFirestore, collection, addDoc, deleteDoc, updateDoc, doc, onSnapshot, query, orderBy, setDoc, getDoc, getDocs, deleteField, initializeFirestore, persistentLocalCache, persistentMultipleTabManager, arrayUnion, where, writeBatch, arrayRemove
} from "firebase/firestore"; 
import { 
  getAuth, signInWithEmailAndPassword, deleteUser, createUserWithEmailAndPassword, signOut, onAuthStateChanged 
} from "firebase/auth";
import React, { useContext, useState, useEffect } from "react";
import { firebaseConfig } from "./firebaseConfig";
import { getAnalytics, isSupported } from "firebase/analytics";

const app = initializeApp(firebaseConfig);

export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({tabManager: persistentMultipleTabManager()})
});
const auth = getAuth(app);

export let analytics;
isSupported().then((supported) => {
    if (supported) {
        analytics = getAnalytics(app);
    }
});

const DataContext = React.createContext();

export function useData() {
  return useContext(DataContext);
}

// Updated Defaults
const DEFAULT_NAV = [
    { title: "Home", link: "/", visible: true, type: 'system' },
    { title: "Full Schedule", link: "/schedule", visible: true, type: 'system' },
    { title: "Standings & Results", link: "/standings", visible: true, type: 'system' },
    { title: "Past Streams", link: "/archive", visible: true, type: 'system' },
    { title: "Conditions of Contest", link: "/rules", visible: true, type: 'system' },
    { title: "Donate", link: "/donate", visible: false, type: 'system' },
    { title: "Analysis Board", link: "/analysis", visible: false, type: 'system' },
];

export function DataProvider({ children }) {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null); // NEW: Stores { role: 'admin'|'captain', teamId: '...', email: '...' }
    
    const [schedule, setSchedule] = useState([]);
    const [conditionsText, setConditionsText] = useState(null);
    const [teams, setTeams] = useState([]); 
    const [standings, setStandings] = useState([]);
    const [homeContent, setHomeContent] = useState({ welcomeText: "", welcomeVideo: "" });
    const [chunkMap, setChunkMap] = useState({});
    const [donationsContent, setDonationsContent] = useState({ text: "", link: "" });
    const [allLineups, setAllLineups] = useState({});
    const [aliasesCsv, setAliasesCsv] = useState("");
    
    const [navigation, setNavigation] = useState(DEFAULT_NAV);
    const [customPages, setCustomPages] = useState([]); 
    const [globalStyle, setGlobalStyle] = useState(null);
    const [allUsers, setAllUsers] = useState([]); // NEW: List of all users for Admin management

    // Loading States
    const [authReady, setAuthReady] = useState(false);
    const [navReady, setNavReady] = useState(false);
    const [brackets, setBrackets] = useState({ main: {}, consolation: {} });
    const loading = !authReady || !navReady;

    const [themePresets, setThemePresets] = useState([]);

    const [isVisible, setIsVisible] = useState(document.visibilityState === 'visible');

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsVisible(document.visibilityState === 'visible');
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    // 1. Auth & Role Listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                // Fetch Role
                try {
                    const rolesDoc = await getDoc(doc(db, "system", "roles"));
                    if (rolesDoc.exists() && rolesDoc.data()[currentUser.uid]) {
                        // Extract just this user's data from the master list
                        setUserRole(rolesDoc.data()[currentUser.uid]);
                    } else {
                        // Fallback for legacy admins
                        setUserRole({ role: 'admin' }); 
                    }
                } catch (err) {
                    console.warn("Offline or missing cache: Could not fetch user role.", err);
                    setUserRole(null); // Safely default to standard user if offline
                }
            } else {
                setUser(null);
                setUserRole(null);
            }
            setAuthReady(true);
        });
        return unsubscribe;
    }, []);

    // 2. Schedule Listener
    useEffect(() => {
        if (!isVisible) return;

        // NEW: Listen to the schedule_chunks collection instead of a single document
        const unsubscribe = onSnapshot(collection(db, "schedule_chunks"), (snapshot) => {
            let allEvents = [];
            let newChunkMap = {};
            
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                newChunkMap[docSnap.id] = data; // Keep raw data in memory to calculate sizes later
                
                if (data.events) {
                    const eventsMap = data.events;
                    const chunkEvents = Object.keys(eventsMap).map(key => ({
                        chunkId: docSnap.id, 
                        ...eventsMap[key],
                        id: key // 🚨 MOVED TO BOTTOM: Forces the true database key to overwrite corrupted payload IDs!
                    }));
                    allEvents.push(...chunkEvents);
                }
            });
            
            allEvents.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
            setSchedule(allEvents);
            setChunkMap(newChunkMap);
        });
        
        return unsubscribe;
    }, [isVisible]);

    // 3. Settings Listeners
    useEffect(() => {
        if (!isVisible) return; 

        const unsubs = [];

        // 1. The single master listener for all configuration data (Public)
        unsubs.push(
            onSnapshot(doc(db, "system", "config"), (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setConditionsText(data.general?.conditions || "");
                    setTeams(data.teams?.list || []);
                    setStandings(data.standings?.data || []);
                    setHomeContent(data.home || {});
                    setDonationsContent(data.donations || {});
                    setGlobalStyle(data.style || null);
                    setBrackets(data.brackets || { main: {}, consolation: {} });
                    setAllLineups(data.lineups || {});
                    setAliasesCsv(data.aliases?.csv || "");
                    
                    if (data.navigation?.items) {
                        const dbItems = data.navigation.items;
                        const mergedItems = [...dbItems];
                        DEFAULT_NAV.forEach(defItem => {
                            if (!dbItems.find(i => i.link === defItem.link)) mergedItems.push(defItem);
                        });
                        setNavigation(mergedItems);
                    } else {
                        setNavigation(DEFAULT_NAV);
                    }
                    setNavReady(true);
                } else {
                    setNavReady(true);
                }
            })
        );

        // 2. The custom pages listener (Still a collection)
        unsubs.push(
            onSnapshot(collection(db, "pages"), snap => setCustomPages(snap.docs.map(d => ({id: d.id, ...d.data()}))))
        );

        // 3. The Roles listener (PRIVATE - Only attach if Staff!)
        // This prevents public users from getting "Permission Denied" console errors
        if (userRole && (userRole.role === 'admin' || userRole.role === 'captain')) {
            unsubs.push(
                onSnapshot(doc(db, "system", "roles"), (snap) => {
                    if (snap.exists()) {
                        const data = snap.data();
                        const usersArray = Object.keys(data).map(uid => ({ uid: uid, ...data[uid] }));
                        setAllUsers(usersArray);
                    } else {
                        setAllUsers([]);
                    }
                })
            );
            if (userRole.role === 'admin'){
                unsubs.push(
                    onSnapshot(doc(db, "system", "themePresets"), (snap) => {
                        if (snap.exists()) {
                            setThemePresets(snap.data().presets || []);
                        } else {
                            setThemePresets([]);
                        }
                    })
                );
            }
        }

        return () => unsubs.forEach(u => u());
    }, [isVisible, userRole]);

    // --- Actions ---
    const login = (e, p) => signInWithEmailAndPassword(auth, e, p);
    
    // UPDATED REGISTER: Now accepts 'role' and creates a user_roles document
    const register = async (email, password, role = 'admin') => {
        // 1. Create the user FIRST so Firebase gives them a token to read the database
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        
        // 2. NOW check the hard limit (They are temporarily authorized to read it)
        const rolesDoc = await getDoc(doc(db, "system", "roles"));
        if (rolesDoc.exists() && Object.keys(rolesDoc.data()).length >= 50) {
            
            // 3. ROLLBACK: Delete the account we just created
            await deleteUser(cred.user);
            throw new Error("Registration closed: Maximum of 50 authorized users reached. Please reach out to the organizers to increase this limit");
        }

        // 4. SUCCESS: Save them to the master roles document
        await setDoc(doc(db, "system", "roles"), {
            [cred.user.uid]: {
                email: email,
                role: role,
                teamId: "" 
            }
        }, { merge: true }); // setDoc with merge is safe here because the UID is a top-level key!
        
        return cred;
    };
    
    const logout = () => signOut(auth);

    const updateUserRoleData = async (uid, data) => {
        const updatePayload = {};
        for (const key in data) {
            updatePayload[`${uid}.${key}`] = data[key];
        }
        await updateDoc(doc(db, "system", "roles"), updatePayload);
    };

    const saveSetting = (settingKey, data) => {
        return setDoc(doc(db, "system", "config"), { 
            [settingKey]: data 
        }, { merge: true });
    };

    // NEW: Update User Data (For Admin to assign teams)
    // const updateUserRoleData = async (uid, data) => {
    //     await updateDoc(doc(db, "user_roles", uid), data);
    // };

    const addEvent = async (data) => {
        const newId = doc(collection(db, "dummy")).id; 
        
        // 1. Find the currently active chunk (the one with the highest index)
        const chunkIds = Object.keys(chunkMap);
        let activeChunkId = "chunk_0";
        if (chunkIds.length > 0) {
            chunkIds.sort((a,b) => parseInt(a.split('_')[1] || 0) - parseInt(b.split('_')[1] || 0));
            activeChunkId = chunkIds[chunkIds.length - 1];
        }

        // 2. Calculate the projected byte size in memory
        const activeChunkData = chunkMap[activeChunkId] || { events: {} };
        const projectedData = { ...activeChunkData, events: { ...activeChunkData.events, [newId]: data } };
        const sizeInBytes = new Blob([JSON.stringify(projectedData)]).size;

        // 3. Route to the current chunk or spawn a new one!
        if (sizeInBytes > 900 * 1024) { // Approaching 1MB limit
            const nextIndex = parseInt(activeChunkId.split('_')[1] || 0) + 1;
            await setDoc(doc(db, "schedule_chunks", `chunk_${nextIndex}`), {
                events: { [newId]: data }
            });
        } else {
            if (!chunkMap[activeChunkId]) {
                await setDoc(doc(db, "schedule_chunks", activeChunkId), { events: { [newId]: data } });
            } else {
                await updateDoc(doc(db, "schedule_chunks", activeChunkId), {
                    [`events.${newId}`]: data
                });
            }
        }
    };

    const updateEvent = async (id, data) => {
        const existingEvent = schedule.find(e => e.id === id);
        if (!existingEvent) throw new Error("Event not found");
        const chunkId = existingEvent.chunkId || "chunk_0";

        // 1. Calculate projected size
        const activeChunkData = chunkMap[chunkId];
        const projectedData = JSON.parse(JSON.stringify(activeChunkData)); 
        projectedData.events[id] = { ...projectedData.events[id], ...data };
        const sizeInBytes = new Blob([JSON.stringify(projectedData)]).size;

        if (sizeInBytes > 900 * 1024) { // Safely below 1MB
            // ATOMIC MOVE: Use a Write Batch to prevent cloning
            const batch = writeBatch(db);
            
            const chunkIds = Object.keys(chunkMap).sort((a,b) => parseInt(a.split('_')[1] || 0) - parseInt(b.split('_')[1] || 0));
            const highestChunkId = chunkIds[chunkIds.length - 1] || "chunk_0";
            const nextChunkId = `chunk_${parseInt(highestChunkId.split('_')[1] || 0) + 1}`;
            
            // Queue the delete and the new write
            batch.update(doc(db, "schedule_chunks", chunkId), { [`events.${id}`]: deleteField() });
            batch.set(doc(db, "schedule_chunks", nextChunkId), { events: { [id]: projectedData.events[id] } }, { merge: true });
            
            // Execute both at exactly the same time
            await batch.commit();
        } else {
            // SAFE: Update in place
            const updatePayload = {};
            for (const key in data) {
                updatePayload[`events.${id}.${key}`] = data[key];
            }
            await updateDoc(doc(db, "schedule_chunks", chunkId), updatePayload);
        }
    };

    const deleteEvent = async (id) => {
        const existingEvent = schedule.find(e => e.id === id);
        if (!existingEvent) return;
        
        await updateDoc(doc(db, "schedule_chunks", existingEvent.chunkId || "chunk_0"), {
            [`events.${id}`]: deleteField()
        });
    };

    const toggleCommentary = async (matchId, userEmail) => {
        const matchData = schedule.find(m => m.id === matchId);
        if (!matchData) return;

        const currentSignups = matchData.commentators || [];
        let updated;

        if (currentSignups.includes(userEmail)) {
            updated = currentSignups.filter(email => email !== userEmail);
        } else if (currentSignups.length < 2) {
            updated = [...currentSignups, userEmail];
        } else {
            throw new Error("This match already has 2 commentators.");
        }

        await updateDoc(doc(db, "schedule_chunks", matchData.chunkId || "chunk_0"), { 
            [`events.${matchId}.commentators`]: updated 
        });
    };

    // const saveSetting = (docName, data) => setDoc(doc(db, "settings", docName), data, { merge: true });
    const updateConditions = (t) => saveSetting("general", { conditions: t });
    const saveTeams = (l) => saveSetting("teams", { list: l });
    const saveStandings = (d) => saveSetting("standings", { data: d });
    const saveBrackets = (d) => saveSetting("brackets", d);
    const saveHomeContent = (d) => saveSetting("home", d);
    const saveDonationsContent = (d) => saveSetting("donations", d);
    const saveNavigation = (items) => saveSetting("navigation", { items });
    const saveGlobalStyle = (d) => saveSetting("style", d);
    const saveAliasesCsv = (csv) => saveSetting("aliases", { csv });

    const addCustomPage = (data) => addDoc(collection(db, "pages"), data);
    const updateCustomPage = (id, data) => updateDoc(doc(db, "pages", id), data);
    const deleteCustomPage = (id) => deleteDoc(doc(db, "pages", id));

    const saveThemePresets = async (presetsArray) => {
        await setDoc(doc(db, "system", "themePresets"), { presets: presetsArray });
    };

    const saveMatchLineup = async (matchId, teamId, players) => {
        // CHANGED: Using updateDoc so Firestore respects the nested dot-notation
        await updateDoc(doc(db, "system", "config"), { 
            [`lineups.${matchId}.${teamId}`]: players, 
            [`lineups.${matchId}.lastUpdated`]: new Date() 
        });
    };

    const createAnalysisBoard = async (newId, boardData) => {
        await setDoc(doc(db, "analysisBoards", newId), boardData);
    };

    const updateAnalysisBoard = async (boardId, updateData) => {
        await updateDoc(doc(db, "analysisBoards", boardId), updateData);
    };

    const deleteAnalysisBoard = async (boardId) => {
        await deleteDoc(doc(db, "analysisBoards", boardId));
    };

    // --- SHARING ---
    const inviteToAnalysisBoard = async (boardId, email) => {
        // arrayUnion safely adds the email without duplicating it
        await updateDoc(doc(db, "analysisBoards", boardId), {
            allowedUsers: arrayUnion(email.trim().toLowerCase())
        });
    };

    const uninviteFromAnalysisBoard = async (boardId, email) => {
        // arrayRemove safely pulls the email out of the allowed list
        await updateDoc(doc(db, "analysisBoards", boardId), {
            allowedUsers: arrayRemove(email.trim().toLowerCase())
        });
    };

    // --- SUBSCRIPTIONS ---
    const subscribeToAnalysisLobby = (currentUser, isAdmin, callback) => {
        if (!currentUser) return () => {};
        
        const boardsRef = collection(db, "analysisBoards");
        
        // Admins see all boards. 
        // Regular users see boards they created AND boards shared with them, 
        // because their email will be in the 'allowedUsers' array.
        const q = isAdmin 
            ? boardsRef 
            : query(boardsRef, where("allowedUsers", "array-contains", currentUser.email.toLowerCase()));
        
        return onSnapshot(q, callback);
    };

    const subscribeToAnalysisBoard = (boardId, callback) => {
        if (!boardId) return () => {};
        return onSnapshot(doc(db, "analysisBoards", boardId), callback);
    };
    

    const value = {
        user, userRole, allUsers, schedule, conditionsText, teams, standings, homeContent, navigation, donationsContent, customPages, globalStyle, loading, brackets, allLineups, themePresets,
        login, register, logout, addEvent, updateEvent, deleteEvent, 
        updateConditions, saveTeams, saveStandings, saveHomeContent, saveNavigation, saveDonationsContent, saveAliasesCsv,
        addCustomPage, updateCustomPage, deleteCustomPage, saveGlobalStyle, updateUserRoleData,
        saveBrackets, saveMatchLineup, toggleCommentary, createAnalysisBoard, updateAnalysisBoard, deleteAnalysisBoard, inviteToAnalysisBoard, subscribeToAnalysisLobby, subscribeToAnalysisBoard, uninviteFromAnalysisBoard, saveThemePresets
    };

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
}